<template>
  <v-container>
    <v-row class="mb-6">
      <v-col cols="12" sm="6">
        <h1>People</h1>
        <p class="text-body2 text-medium-emphasis">Your bookable roster</p>
      </v-col>
      <v-col cols="12" sm="6" class="text-right">
        <v-btn color="primary" @click="openCreate">
          <v-icon start>mdi-account-plus</v-icon>
          Add Person
        </v-btn>
      </v-col>
    </v-row>

    <v-alert v-if="error" type="error" closable @click:close="error = null">
      {{ error }}
    </v-alert>

    <v-row v-if="isLoading">
      <v-col cols="12"><v-skeleton-loader type="table-tbody" /></v-col>
    </v-row>

    <v-row v-else-if="people.length === 0">
      <v-col cols="12">
        <v-card>
          <v-card-text class="text-center py-12">
            <v-icon size="48" class="text-medium-emphasis mb-4">mdi-account-group-outline</v-icon>
            <p class="text-h6">No people yet</p>
            <p class="text-body2 text-medium-emphasis">Add your first crew member to start booking</p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Pending invites: sent, not yet claimed by a signup -->
    <v-row v-if="isOwnerOrAdmin && invites.length > 0" class="mb-2">
      <v-col cols="12">
        <v-card variant="tonal">
          <v-card-title class="text-subtitle-1">Pending Invites</v-card-title>
          <v-list density="compact">
            <v-list-item v-for="inv in invites" :key="inv.email">
              <v-list-item-title>{{ inv.email }}</v-list-item-title>
              <v-list-item-subtitle>Invited as {{ inv.role }}</v-list-item-subtitle>
              <template #append>
                <v-btn size="small" variant="text" color="error" @click="revokeInvite(inv.email)">Revoke</v-btn>
              </template>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="!isLoading && people.length > 0">
      <v-col v-for="person in people" :key="person.id" cols="12" md="6" lg="4">
        <v-card class="h-100">
          <v-card-item>
            <template #title>{{ person.name }}</template>
            <template #subtitle>
              <span v-if="memberRole(person)" class="text-success">{{ memberRole(person) }}</span>
              <span v-else-if="person.userId">Linked account, not a member</span>
              <span v-else>No account</span>
            </template>
            <template #append>
              <v-btn
                v-if="!orgStore.isStaff"
                icon="mdi-history"
                size="small"
                variant="text"
                title="View booking history"
                @click="openHistory(person)"
              />
              <v-btn icon="mdi-pencil" size="small" variant="text" color="primary" @click="openEdit(person)" />
              <v-btn icon="mdi-delete" size="small" variant="text" color="error" @click="removePerson(person.id)" />
            </template>
          </v-card-item>
          <v-card-text>
            <p v-if="person.email" class="text-body2">{{ person.email }}</p>
            <p v-if="person.phone" class="text-body2">{{ person.phone }}</p>
            <p v-if="formatAddress(person.address)" class="text-body2 text-medium-emphasis">
              {{ formatAddress(person.address) }}
            </p>
            <div class="mt-2">
              <v-chip v-for="r in person.roles" :key="r" size="x-small" class="mr-1 mb-1">
                {{ r }}<span v-if="defaultRateLabel(person, r)"> — {{ defaultRateLabel(person, r) }}</span>
              </v-chip>
            </div>
            <p v-if="person.notes" class="text-caption text-medium-emphasis mt-2">{{ person.notes }}</p>
            <v-btn
              v-if="isOwnerOrAdmin && !memberRole(person) && person.email"
              size="small"
              variant="tonal"
              class="mt-3"
              @click="openInvite(person)"
            >
              Invite as…
            </v-btn>
            <template v-if="canManageMember(person)">
              <v-btn size="small" variant="tonal" class="mt-3 mr-2" @click="openChangeRole(person)">Change role</v-btn>
              <v-btn size="small" variant="text" color="error" class="mt-3" @click="removeAccess(person)">
                Remove access
              </v-btn>
            </template>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <PersonHistoryDialog
      v-model="showHistoryDialog"
      :org-id="orgId()"
      :person-id="historyPersonId"
      :person-name="historyPersonName"
    />

    <v-dialog v-model="showInviteDialog" width="400">
      <v-card>
        <v-card-title>Invite {{ invitingPerson?.name }}</v-card-title>
        <v-card-text>
          <p class="text-body2 text-medium-emphasis mb-3">{{ invitingPerson?.email }}</p>
          <v-select
            v-model="inviteRole"
            label="Role"
            :items="grantableRoles"
          />
          <p class="text-caption text-medium-emphasis">
            We'll email them. If they already have an account they're added right away; otherwise they're
            added when they first sign in with this email.
          </p>
          <div class="text-right mt-2">
            <v-btn text @click="showInviteDialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="inviting" @click="sendInvite">Send Invite</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showRoleDialog" width="400">
      <v-card>
        <v-card-title>Change role for {{ roleTarget?.name }}</v-card-title>
        <v-card-text>
          <v-select v-model="newRole" label="Role" :items="grantableRoles" />
          <div class="text-right mt-2">
            <v-btn text @click="showRoleDialog = false">Cancel</v-btn>
            <v-btn color="primary" :loading="changingRole" @click="saveRole">Save</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDialog" width="500">
      <v-card>
        <v-card-title>{{ editingId ? 'Edit Person' : 'Add Person' }}</v-card-title>
        <v-card-text>
          <v-form @submit.prevent="save">
            <v-text-field v-model="form.name" label="Name" required class="mb-3" />
            <v-text-field v-model="form.email" label="Email" type="email" class="mb-3" />
            <v-text-field v-model="form.phone" label="Phone" class="mb-3" />
            <v-text-field v-model="form.street" label="Street" class="mb-3" />
            <v-row dense class="mb-1">
              <v-col cols="6"><v-text-field v-model="form.city" label="City" /></v-col>
              <v-col cols="3"><v-text-field v-model="form.state" label="State" /></v-col>
              <v-col cols="3"><v-text-field v-model="form.postalCode" label="Zip" /></v-col>
            </v-row>
            <v-combobox
              v-model="form.roles"
              label="Roles"
              multiple
              chips
              closable-chips
              class="mb-3"
              hint="e.g. FOH Engineer, Stagehand"
            />
            <template v-if="form.roles.length">
              <p class="text-caption text-medium-emphasis mb-2">
                Default rate per role — pre-fills when assigning this person; still editable per gig
              </p>
              <v-row v-for="role in form.roles" :key="role" dense align="center" class="mb-1">
                <v-col cols="4" class="text-body2 text-truncate">{{ role }}</v-col>
                <v-col cols="4">
                  <v-text-field
                    v-model.number="rateRow(role).dayRate"
                    label="Day $"
                    type="number"
                    density="compact"
                    hide-details
                  />
                </v-col>
                <v-col cols="4">
                  <v-text-field
                    v-model.number="rateRow(role).hourlyRate"
                    label="Hourly $"
                    type="number"
                    density="compact"
                    hide-details
                  />
                </v-col>
              </v-row>
            </template>
            <v-textarea v-model="form.notes" label="Notes" rows="2" class="mt-3 mb-3" />
            <div class="text-right">
              <v-btn text @click="showDialog = false">Cancel</v-btn>
              <v-btn color="primary" type="submit" :loading="saving">Save</v-btn>
            </div>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import type { Person, Address, OrgMember, Invite, Role } from '@gigdriver/domain';
import { getOrgMembers, getOrgInvites, createInvite, deleteInvite, updateMemberRole, removeMember } from '@gigdriver/client';
import { usePeopleStore } from '../stores/people';
import { useOrganizationStore } from '../stores/organization';
import { useAuthStore } from '../stores/auth';
import PersonHistoryDialog from '../components/PersonHistoryDialog.vue';

const route = useRoute();
const peopleStore = usePeopleStore();
const orgStore = useOrganizationStore();
const authStore = useAuthStore();

const people = computed(() => peopleStore.people);
const isLoading = computed(() => peopleStore.isLoading);
const error = ref<string | null>(null);

const isOwnerOrAdmin = computed(() => orgStore.myRole === 'owner' || orgStore.myRole === 'admin');
// Only an owner can hand out (or touch) the owner role; mirrors the server-side
// check in functions/src/membersCore.ts, which is the real enforcement.
const grantableRoles = computed<Role[]>(() =>
  orgStore.myRole === 'owner'
    ? ['staff', 'coordinator', 'manager', 'admin', 'owner']
    : ['staff', 'coordinator', 'manager', 'admin'],
);
const members = ref<Record<string, OrgMember>>({});
const invites = ref<Invite[]>([]);

const showHistoryDialog = ref(false);
const historyPersonId = ref('');
const historyPersonName = ref('');

function openHistory(person: Person) {
  historyPersonId.value = person.id;
  historyPersonName.value = person.name;
  showHistoryDialog.value = true;
}

function memberRole(person: Person): string | null {
  if (!person.userId) return null;
  return members.value[person.userId]?.role ?? null;
}

async function loadMembersAndInvites() {
  if (!orgId() || !isOwnerOrAdmin.value) return;
  [members.value, invites.value] = await Promise.all([getOrgMembers(orgId()), getOrgInvites(orgId())]);
}

function canManageMember(person: Person): boolean {
  if (!isOwnerOrAdmin.value || !person.userId) return false;
  const role = members.value[person.userId]?.role;
  if (!role) return false;
  if (person.userId === authStore.currentUser?.uid) return false;
  return !(role === 'owner' && orgStore.myRole !== 'owner');
}

const showRoleDialog = ref(false);
const roleTarget = ref<Person | null>(null);
const newRole = ref<Role>('staff');
const changingRole = ref(false);

function openChangeRole(person: Person) {
  roleTarget.value = person;
  newRole.value = members.value[person.userId!]?.role ?? 'staff';
  showRoleDialog.value = true;
}

async function saveRole() {
  if (!roleTarget.value?.userId) return;
  try {
    changingRole.value = true;
    error.value = null;
    await updateMemberRole({ orgId: orgId(), uid: roleTarget.value.userId, role: newRole.value });
    showRoleDialog.value = false;
    await loadMembersAndInvites();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to change role';
  } finally {
    changingRole.value = false;
  }
}

async function removeAccess(person: Person) {
  if (!person.userId) return;
  if (!confirm(`Remove ${person.name}'s access to this organization? They stay on the roster and can be re-invited.`)) return;
  try {
    error.value = null;
    await removeMember({ orgId: orgId(), uid: person.userId });
    await loadMembersAndInvites();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to remove access';
  }
}

const showInviteDialog = ref(false);
const invitingPerson = ref<Person | null>(null);
const inviteRole = ref<Role>('staff');
const inviting = ref(false);

function openInvite(person: Person) {
  invitingPerson.value = person;
  inviteRole.value = 'staff';
  showInviteDialog.value = true;
}

async function sendInvite() {
  if (!invitingPerson.value?.email || !authStore.currentUser) return;
  try {
    inviting.value = true;
    error.value = null;
    await createInvite(orgId(), invitingPerson.value.email, inviteRole.value, authStore.currentUser.uid);
    showInviteDialog.value = false;
    await loadMembersAndInvites();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to send invite';
  } finally {
    inviting.value = false;
  }
}

async function revokeInvite(email: string) {
  if (!confirm(`Revoke the invite for ${email}?`)) return;
  try {
    error.value = null;
    await deleteInvite(orgId(), email);
    await loadMembersAndInvites();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to revoke invite';
  }
}

const showDialog = ref(false);
const saving = ref(false);
const editingId = ref<string | null>(null);
const form = reactive({
  name: '',
  email: '',
  phone: '',
  street: '',
  city: '',
  state: '',
  postalCode: '',
  roles: [] as string[],
  notes: '',
});

// This person's default day/hourly rate per role, keyed by role name —
// separate from `form` since it's keyed dynamically by the roles list.
const roleRates = reactive<Record<string, { dayRate: number; hourlyRate: number }>>({});

function rateRow(role: string) {
  return (roleRates[role] ??= { dayRate: 0, hourlyRate: 0 });
}

function defaultRateLabel(person: Person, role: string): string {
  const r = person.defaultRates?.[role];
  if (!r) return '';
  const parts: string[] = [];
  if (r.dayRate?.amount) parts.push(`$${r.dayRate.amount}/day`);
  if (r.hourlyRate?.amount) parts.push(`$${r.hourlyRate.amount}/hr`);
  return parts.join(', ');
}

const orgId = () => route.query.org as string;

onMounted(async () => {
  if (orgId()) {
    await peopleStore.loadOrgPeople(orgId());
    await loadMembersAndInvites();
  }
});

function formatAddress(a?: Address): string {
  if (!a) return '';
  const line2 = [a.city, a.state, a.postalCode].filter(Boolean).join(' ');
  return [a.street, line2].filter(Boolean).join(', ');
}

function resetForm() {
  form.name = '';
  form.email = '';
  form.phone = '';
  form.street = '';
  form.city = '';
  form.state = '';
  form.postalCode = '';
  form.roles = [];
  form.notes = '';
  for (const key of Object.keys(roleRates)) delete roleRates[key];
}

function openCreate() {
  editingId.value = null;
  resetForm();
  showDialog.value = true;
}

function openEdit(person: Person) {
  editingId.value = person.id;
  form.name = person.name;
  form.email = person.email ?? '';
  form.phone = person.phone ?? '';
  form.street = person.address?.street ?? '';
  form.city = person.address?.city ?? '';
  form.state = person.address?.state ?? '';
  form.postalCode = person.address?.postalCode ?? '';
  form.roles = [...person.roles];
  form.notes = person.notes ?? '';
  for (const key of Object.keys(roleRates)) delete roleRates[key];
  for (const role of form.roles) {
    const r = person.defaultRates?.[role];
    roleRates[role] = { dayRate: r?.dayRate?.amount ?? 0, hourlyRate: r?.hourlyRate?.amount ?? 0 };
  }
  showDialog.value = true;
}

async function save() {
  try {
    saving.value = true;
    error.value = null;
    const address: Address = {
      street: form.street || undefined,
      city: form.city || undefined,
      state: form.state || undefined,
      postalCode: form.postalCode || undefined,
    };
    const hasAddress = Object.values(address).some(Boolean);
    const defaultRates: NonNullable<Person['defaultRates']> = {};
    for (const role of form.roles) {
      const r = roleRates[role];
      if (!r || (!r.dayRate && !r.hourlyRate)) continue;
      defaultRates[role] = {
        dayRate: r.dayRate ? { amount: r.dayRate, currency: 'USD' } : undefined,
        hourlyRate: r.hourlyRate ? { amount: r.hourlyRate, currency: 'USD' } : undefined,
      };
    }
    const payload = {
      name: form.name,
      email: form.email || undefined,
      phone: form.phone || undefined,
      address: hasAddress ? address : undefined,
      roles: form.roles,
      defaultRates: Object.keys(defaultRates).length ? defaultRates : undefined,
      notes: form.notes || undefined,
    };
    if (editingId.value) {
      await peopleStore.updatePerson(orgId(), editingId.value, payload);
    } else {
      await peopleStore.createPerson(orgId(), payload);
    }
    showDialog.value = false;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to save';
  } finally {
    saving.value = false;
  }
}

async function removePerson(id: string) {
  if (!confirm('Delete this person?')) return;
  try {
    error.value = null;
    await peopleStore.deletePerson(orgId(), id);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to delete';
  }
}
</script>
